import type { PageLogicCondition, PageLogicOperator } from "@patient-watch/forms";
import { NUMERIC_PAGE_LOGIC_OPERATORS } from "@patient-watch/forms";
import { withResolvedChoices } from "@/features/dictionaries/inputChoices";
import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";
import { ui } from "@patient-watch/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import type { InputT } from "@patient-watch/types";
import { useEffect, useState } from "react";

export interface LogicOption {
    label: string;
    value: string;
}

export interface PriorLogicInput {
    input_id: string;
    label: string;
    pageLabel: string;
    input_key: string | null;
    options: LogicOption[];
}

const CHOICE_INPUT_KEYS = new Set(["single-choice", "dropdown", "multiple-choice", "scale-rating", "yes-no"]);

/** Questions a rule can check: those on or before `pageId`, or every question when `pageId` is omitted. */
export function collectPriorInputs(
    form: FormBuilderData,
    pageId: string | undefined,
    withChoices: Map<string, InputT>
): PriorLogicInput[] {
    const sortedPages = [...form.pages].sort((a, b) => a.page_index - b.page_index);
    const targetIndex = sortedPages.findIndex((page) => page.page_id === pageId);
    const inputs: PriorLogicInput[] = [];

    for (const page of sortedPages) {
        if (targetIndex >= 0 && page.page_index > sortedPages[targetIndex]!.page_index) break;
        for (const rawInput of page.inputs) {
            // Dictionary-bound inputs hold a dictionary_id; their choices come from the fetched rows.
            const input = withResolvedChoices(withChoices.get(rawInput.input_id) ?? (rawInput as InputT));
            inputs.push({
                input_id: input.input_id,
                label: input.input_label ?? "Untitled question",
                pageLabel: page.page_label ?? `Page ${page.page_index}`,
                input_key: input.input_key,
                options: optionsForInput(input.input_key, input.options, input.scale_options),
            });
        }
    }

    return inputs;
}

function parseScaleOptions(raw: unknown): LogicOption[] {
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((item) => {
        if (!item || typeof item !== "object") return [];
        const record = item as { label?: unknown; value?: unknown };
        if (record.label == null || record.value == null || record.value === "") return [];
        return [{ label: String(record.label), value: String(record.value) }];
    });
}

function optionsForInput(inputKey: string | null, options: string[] | null, scaleOptions: unknown): LogicOption[] {
    if (inputKey === "yes-no") {
        return [
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" },
        ];
    }

    const fromScale = parseScaleOptions(scaleOptions);
    if (fromScale.length > 0) return fromScale;

    return (options ?? [])
        .filter((option) => option.trim().length > 0)
        .map((option) => ({ label: option, value: option }));
}

function isNumericInputKey(inputKey: string | null | undefined): boolean {
    return inputKey === "number" || inputKey === "slider";
}

function usesOptionPicker(
    inputKey: string | null | undefined,
    operator: PageLogicOperator,
    optionCount: number
): boolean {
    if (optionCount === 0 || !CHOICE_INPUT_KEYS.has(inputKey ?? "")) return false;
    if (inputKey === "scale-rating" && NUMERIC_PAGE_LOGIC_OPERATORS.includes(operator)) return false;
    return true;
}

export function defaultOperator(inputKey: string | null | undefined): PageLogicOperator {
    return inputKey === "multiple-choice" ? "includes" : "equals";
}

export function defaultRuleValue(inputKey: string | null | undefined): string {
    return inputKey === "yes-no" ? "yes" : "";
}

export function commitRuleValue(
    operator: PageLogicOperator,
    nextValue: string,
    inputKey?: string | null
): string | number {
    const storeAsNumber =
        NUMERIC_PAGE_LOGIC_OPERATORS.includes(operator) || inputKey === "scale-rating" || inputKey === "number";
    if (!storeAsNumber) return nextValue;
    const parsed = Number(nextValue);
    return nextValue.trim() !== "" && Number.isFinite(parsed) ? parsed : nextValue;
}

function RuleAnswerValue({
    inputKey,
    operator,
    value,
    options,
    onCommit,
}: {
    inputKey: string | null | undefined;
    operator: PageLogicOperator;
    value: string;
    options: LogicOption[];
    onCommit: (next: string) => void;
}) {
    const [draft, setDraft] = useState(value);

    useEffect(() => {
        setDraft(value);
    }, [value]);

    if (usesOptionPicker(inputKey, operator, options.length)) {
        const selected = options.some((option) => option.value === value) ? value : undefined;
        return (
            <Select value={selected} onValueChange={onCommit}>
                <SelectTrigger
                    size="sm"
                    className="h-8 min-h-8 max-h-8 text-xs"
                    data-testid="page-logic-answer-options"
                >
                    <SelectValue placeholder="Choose an option" />
                </SelectTrigger>
                <SelectContent>
                    {options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }

    const useNumberField = NUMERIC_PAGE_LOGIC_OPERATORS.includes(operator) || isNumericInputKey(inputKey);

    return (
        <ui.Input
            className="h-8 min-h-8 text-xs"
            data-testid={useNumberField ? "page-logic-answer-number" : "page-logic-answer-text"}
            type={useNumberField ? "number" : "text"}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
                if (draft !== value) onCommit(draft);
            }}
            onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
            }}
            placeholder={useNumberField ? "Number, e.g. 50" : "Stored answer value"}
        />
    );
}

/** Question, operator and answer for one condition. Shared by page logic and screener criteria. */
export function LogicConditionFields({
    inputs,
    condition,
    onChange,
}: {
    inputs: PriorLogicInput[];
    condition: PageLogicCondition;
    onChange: (next: PageLogicCondition) => void;
}) {
    const selected = inputs.find((input) => input.input_id === condition.input_id);

    return (
        <>
            <Select
                value={condition.input_id}
                onValueChange={(inputId) => {
                    const next = inputs.find((input) => input.input_id === inputId);
                    onChange({
                        input_id: inputId,
                        operator: defaultOperator(next?.input_key),
                        value: defaultRuleValue(next?.input_key),
                    });
                }}
            >
                <SelectTrigger size="sm" className="h-8 min-h-8 max-h-8 text-xs">
                    <SelectValue placeholder="Question" />
                </SelectTrigger>
                <SelectContent>
                    {inputs.map((input) => (
                        <SelectItem key={input.input_id} value={input.input_id}>
                            {input.pageLabel}: {input.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Select
                value={condition.operator}
                onValueChange={(operator) => onChange({ ...condition, operator: operator as PageLogicOperator })}
            >
                <SelectTrigger size="sm" className="h-8 min-h-8 max-h-8 text-xs">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="equals">equals</SelectItem>
                    <SelectItem value="not_equals">does not equal</SelectItem>
                    <SelectItem value="includes">includes</SelectItem>
                    <SelectItem value="greater_than">greater than</SelectItem>
                    <SelectItem value="greater_than_or_equals">greater than or equal to</SelectItem>
                    <SelectItem value="less_than">less than</SelectItem>
                    <SelectItem value="less_than_or_equals">less than or equal to</SelectItem>
                </SelectContent>
            </Select>

            <RuleAnswerValue
                inputKey={selected?.input_key}
                operator={condition.operator}
                options={selected?.options ?? []}
                value={String(condition.value ?? "")}
                onCommit={(nextValue) =>
                    onChange({
                        ...condition,
                        value: commitRuleValue(condition.operator, nextValue, selected?.input_key),
                    })
                }
            />
        </>
    );
}
